import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Card,
  InputAdornment
} from '@mui/material';
import {
  CheckCircle,
  ReceiptLongOutlined,
  SecurityOutlined,
  SearchOutlined,
  CloudUpload,
  FolderSpecial,
  InsertDriveFile,
  LockOutlined
} from '@mui/icons-material';
import { API_BASE } from '../context/AuthContext';

declare global {
  interface Window {
    Razorpay: any;
  }
}

interface ParsedFile {
  name: string;
  type: string;
  base64: string;
}

interface InvoiceLead {
  leadId: string;
  name: string;
  phone: string;
  email: string;
  serviceName: string;
  source: string;
  quotedAmount: number;
  discountPercent: number;
  gstIncluded: boolean;
  finalAmount: number;
  paymentStatus: 'pending' | 'paid' | 'failed';
  paymentToken: string;
  paymentCode: string;
  requestedDocuments: string[];
  paidAt?: string;
}

export const Payment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { token: urlToken } = useParams<{ token?: string }>();
  const navigate = useNavigate();

  const queryRef = urlToken || searchParams.get('token') || searchParams.get('code') || '';

  const [inputCode, setInputCode] = useState(queryRef);
  const [lead, setLead] = useState<InvoiceLead | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>('');
  
  // Payment states
  const [isPaying, setIsPaying] = useState<boolean>(false);
  const [paymentSuccessMsg, setPaymentSuccessMsg] = useState<string>('');
  const [paymentError, setPaymentError] = useState<string>('');

  // Document upload states (active after payment)
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File>>({});
  const [isUploadingDocs, setIsUploadingDocs] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [driveFolderLink, setDriveFolderLink] = useState<string>('');

  const fetchInvoice = async (codeOrToken: string) => {
    if (!codeOrToken.trim()) return;
    setLoading(true);
    setSearchError('');
    setPaymentError('');

    try {
      const res = await fetch(`${API_BASE}/api/payment-info/${encodeURIComponent(codeOrToken.trim())}`);
      const data = await res.json();

      if (res.ok && data.success) {
        setLead(data.lead);
      } else {
        setLead(null);
        setSearchError(data.message || 'Invoice or quotation reference not found.');
      }
    } catch (err: any) {
      setLead(null);
      setSearchError('Network error connecting to payment gateway. Please verify your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (queryRef) {
      fetchInvoice(queryRef);
    }
  }, [queryRef]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputCode.trim()) {
      fetchInvoice(inputCode.trim());
    }
  };

  const handleRazorpayPayment = async () => {
    if (!lead) return;
    setIsPaying(true);
    setPaymentError('');

    try {
      // 1. Create order on backend
      const orderRes = await fetch(`${API_BASE}/api/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentToken: lead.paymentToken })
      });

      const orderData = await orderRes.json();
      if (!orderRes.ok || !orderData.success) {
        throw new Error(orderData.message || 'Failed to initialize Razorpay checkout.');
      }

      // 2. Launch Razorpay Checkout Modal
      const options = {
        key: orderData.keyId,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'Fingard Partners',
        description: `Professional CA Services: ${lead.serviceName || 'Tax & Compliance'}`,
        image: '/logo.svg',
        order_id: orderData.order.id,
        prefill: {
          name: lead.name,
          email: lead.email,
          contact: lead.phone
        },
        notes: {
          leadId: lead.leadId,
          paymentCode: lead.paymentCode
        },
        theme: {
          color: '#123D6B'
        },
        handler: async function (response: any) {
          try {
            // 3. Verify payment signature on backend
            const verifyRes = await fetch(`${API_BASE}/api/payment/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paymentToken: lead.paymentToken,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            const verifyData = await verifyRes.json();
            if (verifyRes.ok && verifyData.success) {
              setPaymentSuccessMsg(`Payment of ₹${lead.finalAmount.toLocaleString('en-IN')} confirmed successfully! Txn ID: ${response.razorpay_payment_id}`);
              setLead({
                ...lead,
                paymentStatus: 'paid',
                paidAt: new Date().toISOString()
              });
              localStorage.setItem('fingard_lead_id', lead.leadId);
              localStorage.setItem('fingard_client_name', lead.name);
            } else {
              setPaymentError(verifyData.message || 'Signature verification failed.');
            }
          } catch (err: any) {
            setPaymentError('Error verifying payment on server.');
          } finally {
            setIsPaying(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (resp: any) {
        setIsPaying(false);
        setPaymentError(resp.error?.description || 'Payment was declined or cancelled.');
      });
      rzp.open();
    } catch (err: any) {
      setIsPaying(false);
      setPaymentError(err.message || 'Payment initialization error.');
    }
  };

  const handleFileChange = (docName: string, file: File) => {
    setSelectedFiles(prev => ({ ...prev, [docName]: file }));
  };

  const handleUploadDocuments = async () => {
    if (!lead || Object.keys(selectedFiles).length === 0) return;
    setIsUploadingDocs(true);

    try {
      const filePromises = Object.entries(selectedFiles).map(([docName, file]) => {
        return new Promise<ParsedFile>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve({
              name: `${docName}_${file.name}`,
              type: file.type,
              base64: base64String
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const parsedFiles = await Promise.all(filePromises);

      const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leadId: lead.leadId,
          name: lead.name,
          serviceCategory: lead.source || 'general',
          files: parsedFiles
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setUploadSuccess(true);
        if (data.driveLink) {
          setDriveFolderLink(data.driveLink);
        }
      } else {
        alert(data.error || 'Failed to upload files. Please retry.');
      }
    } catch (err: any) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setIsUploadingDocs(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip
            icon={<ReceiptLongOutlined sx={{ fontSize: '18px !important' }} />}
            label="Secure Client Billing & Document Vault"
            color="primary"
            sx={{ mb: 1.5, bgcolor: '#123D6B', fontWeight: 700 }}
          />
          <Typography variant="h3" fontWeight={800} color="#0B2847" gutterBottom sx={{ letterSpacing: '-0.02em' }}>
            Invoice & Payment Portal
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Enter your Quotation Reference Code provided by your Fingard Advisor to view your customized invoice, complete secure payment, and access your Document Vault.
          </Typography>
        </Box>

        {/* Search / Lookup Bar */}
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: 3,
            border: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            display: 'flex',
            gap: 1.5,
            alignItems: 'center',
            boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)'
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Payment Code (e.g. FP-K8X9Y2 or Lead ID)"
            value={inputCode}
            onChange={(e) => setInputCode(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: '#64748B' }} />
                </InputAdornment>
              )
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !inputCode.trim()}
            sx={{
              bgcolor: '#123D6B',
              px: 3.5,
              py: 1,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#0B2847' }
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Find Invoice'}
          </Button>
        </Paper>

        {searchError && (
          <Alert severity="warning" sx={{ mb: 4, borderRadius: 2 }}>
            {searchError}
          </Alert>
        )}

        {/* If no invoice looked up yet */}
        {!lead && !loading && !searchError && (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              textAlign: 'center',
              bgcolor: '#FFFFFF',
              borderColor: '#E2E8F0'
            }}
          >
            <LockOutlined sx={{ fontSize: 48, color: '#94A3B8', mb: 1.5 }} />
            <Typography variant="h6" fontWeight={700} color="#0B2847" gutterBottom>
              Personalized Quotations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
              Fingard Partners does not display generic prices on the website. Each client receives a tailored price quotation based on specific business scope.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => navigate('/services')}
                sx={{
                  bgcolor: '#123D6B',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#0B2847' }
                }}
              >
                Explore Services & Request Quote
              </Button>
              <Button
                variant="outlined"
                component="a"
                href="https://wa.me/919116033575"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderColor: '#25D366',
                  color: '#25D366',
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                Contact Sales on WhatsApp
              </Button>
            </Box>
          </Paper>
        )}

        {/* Invoice Found Card */}
        {lead && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, md: 4.5 },
                borderRadius: 4,
                border: '1px solid #E2E8F0',
                bgcolor: '#FFFFFF',
                boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)'
              }}
            >
              {/* Invoice Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary" fontWeight={700}>
                    OFFICIAL QUOTATION & INVOICE
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="#0B2847">
                    Fingard Partners LLP
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Jaipur | New Delhi | Bangalore | support@fingard.co
                  </Typography>
                </Box>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Chip
                    label={lead.paymentStatus === 'paid' ? 'PAID & ACTIVATED' : 'PAYMENT PENDING'}
                    color={lead.paymentStatus === 'paid' ? 'success' : 'warning'}
                    sx={{ fontWeight: 800, px: 1, py: 1.5, borderRadius: 2 }}
                  />
                  <Typography variant="body2" fontWeight={700} color="#123D6B" sx={{ mt: 1, fontFamily: 'monospace' }}>
                    Ref: {lead.paymentCode} ({lead.leadId})
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Client & Service Info */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                    BILLED TO:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} color="#0B2847">
                    {lead.name}
                  </Typography>
                  <Typography variant="body2" color="#475569">
                    Phone: {lead.phone}
                  </Typography>
                  {lead.email && (
                    <Typography variant="body2" color="#475569">
                      Email: {lead.email}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">
                    ENGAGEMENT SCOPE:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={700} color="#123D6B">
                    {lead.serviceName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Category: {lead.source}
                  </Typography>
                </Grid>
              </Grid>

              {/* Amount Breakdown Table */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: '#F8FAFC', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Quoted Professional Fee</Typography>
                  <Typography variant="body2" fontWeight={600}>₹{lead.quotedAmount.toLocaleString('en-IN')}</Typography>
                </Box>

                {lead.discountPercent > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: '#10B981' }}>
                    <Typography variant="body2">Advisory Discount ({lead.discountPercent}%)</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      -₹{Math.round((lead.quotedAmount * lead.discountPercent) / 100).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    GST Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {lead.gstIncluded ? '18% GST Included' : 'Statutory GST Applicable'}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight={800} color="#0B2847">Total Amount Payable</Typography>
                  <Typography variant="h4" fontWeight={900} color="#10B981">
                    ₹{lead.finalAmount.toLocaleString('en-IN')}
                  </Typography>
                </Box>
              </Paper>

              {paymentSuccessMsg && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3, borderRadius: 2 }}>
                  {paymentSuccessMsg}
                </Alert>
              )}

              {paymentError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {paymentError}
                </Alert>
              )}

              {/* Action Button: Pay or Paid */}
              {lead.paymentStatus === 'pending' ? (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleRazorpayPayment}
                  disabled={isPaying}
                  startIcon={isPaying ? <CircularProgress size={22} color="inherit" /> : <SecurityOutlined />}
                  sx={{
                    py: 1.8,
                    borderRadius: 2.5,
                    bgcolor: '#123D6B',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#C59B27' }
                  }}
                >
                  {isPaying ? 'Processing Payment...' : `Pay ₹${lead.finalAmount.toLocaleString('en-IN')} Securely via Razorpay →`}
                </Button>
              ) : (
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#ECFDF5', borderRadius: 2.5, border: '1px solid #A7F3D0' }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#065F46">
                    ✓ Payment Completed & Verified
                  </Typography>
                  <Typography variant="caption" color="#047857">
                    Your assigned CA has been notified. Please upload your documents below to start work.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* ======================================================== */}
            {/* POST-PAYMENT DOCUMENT VAULT (Visible once paid)          */}
            {/* ======================================================== */}
            {lead.paymentStatus === 'paid' && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4.5 },
                  borderRadius: 4,
                  border: '1px solid #CBD5E1',
                  bgcolor: '#FFFFFF',
                  boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)'
                }}
              >
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <FolderSpecial sx={{ fontSize: 56, color: '#123D6B', mb: 1 }} />
                  <Typography variant="h5" fontWeight={800} color="#0B2847" gutterBottom>
                    Client Document Vault
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please upload the documents requested by your CA advisor for <strong>{lead.serviceName}</strong>.
                  </Typography>
                </Box>

                {uploadSuccess && (
                  <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                    ✓ Documents successfully uploaded to your secure CA Audit Vault! Your assigned CA has received access.
                    {driveFolderLink && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={driveFolderLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Uploaded Files in Drive
                        </Button>
                      </Box>
                    )}
                  </Alert>
                )}

                <Typography variant="subtitle2" fontWeight={700} color="#0B2847" sx={{ mb: 2 }}>
                  Requested Document Checklist:
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {lead.requestedDocuments.map((docName, idx) => {
                    const isAttached = !!selectedFiles[docName];
                    return (
                      <Grid item xs={12} key={idx}>
                        <Card
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2.5,
                            borderColor: isAttached ? '#10B981' : '#E2E8F0',
                            borderWidth: isAttached ? 2 : 1,
                            bgcolor: isAttached ? 'rgba(16, 185, 129, 0.04)' : '#FFFFFF',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <InsertDriveFile sx={{ color: isAttached ? '#10B981' : '#123D6B' }} />
                            <Box>
                              <Typography variant="subtitle2" fontWeight={700} color="#0B2847">
                                {docName}
                              </Typography>
                              <Typography variant="caption" color={isAttached ? '#10B981' : 'text.secondary'}>
                                {isAttached
                                  ? `✓ ${selectedFiles[docName].name} (${(selectedFiles[docName].size / 1024).toFixed(1)} KB)`
                                  : 'PDF, JPG, PNG or ZIP'}
                              </Typography>
                            </Box>
                          </Box>

                          <Button
                            variant={isAttached ? 'outlined' : 'contained'}
                            component="label"
                            size="small"
                            color={isAttached ? 'success' : 'primary'}
                            startIcon={<CloudUpload />}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 700,
                              bgcolor: isAttached ? 'transparent' : '#123D6B'
                            }}
                          >
                            {isAttached ? 'Change File' : 'Attach File'}
                            <input
                              type="file"
                              hidden
                              accept=".pdf,.png,.jpg,.jpeg,.zip"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleFileChange(docName, e.target.files[0]);
                                }
                              }}
                            />
                          </Button>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleUploadDocuments}
                  disabled={isUploadingDocs || Object.keys(selectedFiles).length === 0}
                  startIcon={isUploadingDocs ? <CircularProgress size={22} color="inherit" /> : <CloudUpload />}
                  sx={{
                    py: 1.6,
                    borderRadius: 2.5,
                    bgcolor: '#123D6B',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#0B2847' }
                  }}
                >
                  {isUploadingDocs
                    ? 'Uploading to Encrypted Vault...'
                    : `Submit ${Object.keys(selectedFiles).length} Document(s) to CA Team →`}
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Payment;
